import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes, faCalendarAlt, faBriefcase, faUsers, faMoneyBillAlt, faChurch, faGraduationCap, faComments, faUserTie, faUser, faTachometerAlt } from '@fortawesome/free-solid-svg-icons';
import HeaderAdmin from './HeaderAdmin';

const Dashboard = () => {
  const [stats] = useState({
    totalUsers: 245,
    activeProjects: 12,
    upcomingEvents: 8,
    revenue: '15,280'
  });

  const recentActivities = [
    { id: 1, action: 'New user registration', time: '2 hours ago', user: 'John Doe' },
    { id: 2, action: 'Project status updated', time: '4 hours ago', project: 'Youth Camp' },
    { id: 3, action: 'Payment received', time: '1 day ago', amount: '$1,200' },
    { id: 4, action: 'New announcement posted', time: '2 days ago', by: 'Admin' },
    { id: 5, action: 'Meeting scheduled', time: '3 days ago', with: 'Planning Committee' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <HeaderAdmin />
      
      {/* Dashboard Content */}
      <div className="pt-20 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Welcome to Your Dashboard!</h1>
            <p className="text-gray-600">Here's what's happening with your ministry today.</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { title: 'Total Members', value: stats.totalUsers, change: '+12%', color: 'from-blue-500 to-cyan-500', icon: faUsers },
              { title: 'Active Projects', value: stats.activeProjects, change: '+3', color: 'from-emerald-500 to-green-500', icon: faBriefcase },
              { title: 'Upcoming Events', value: stats.upcomingEvents, change: '+2', color: 'from-purple-500 to-pink-500', icon: faCalendarAlt },
              { title: 'Total Revenue', value: `$${stats.revenue}`, change: '+18%', color: 'from-amber-500 to-orange-500', icon: faMoneyBillAlt }
            ].map((stat, idx) => (
              <div key={idx} className="bg-white rounded-2xl shadow-lg p-6 transform hover:-translate-y-2 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${stat.color} flex items-center justify-center`}>
                    <FontAwesomeIcon icon={stat.icon} className="text-white text-xl" />
                  </div>
                  <span className="text-sm font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">
                    {stat.change}
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-1">{stat.value}</h3>
                <p className="text-gray-500">{stat.title}</p>
              </div>
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-8 mb-8">
            {/* Quick Actions */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Quick Actions</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { title: 'Create Event', icon: faCalendarAlt, color: 'bg-blue-500', href: '/Planning' },
                    { title: 'Add Member', icon: faUser, color: 'bg-emerald-500', href: '/Director' },
                    { title: 'Post News', icon: faComments, color: 'bg-purple-500', href: '/Communication' },
                    { title: 'Upload Report', icon: faBriefcase, color: 'bg-amber-500', href: '/Secretariat' },
                    { title: 'Schedule Meeting', icon: faUserTie, color: 'bg-cyan-500', href: '/Planning' },
                    { title: 'View Analytics', icon: faGraduationCap, color: 'bg-pink-500', href: '/Education' }
                  ].map((action, idx) => (
                    <Link
                      key={idx}
                      to={action.href}
                      className="bg-gray-50 hover:bg-gray-100 rounded-xl p-4 text-center transform hover:scale-105 transition-all duration-300"
                    >
                      <div className={`${action.color} w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3`}>
                        <FontAwesomeIcon icon={action.icon} className="text-white" />
                      </div>
                      <span className="font-medium text-gray-700">{action.title}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 h-full">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Recent Activity</h2>
                <div className="space-y-4">
                  {recentActivities.map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">{activity.action}</p>
                        <p className="text-sm text-gray-500">{activity.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Upcoming Events & Projects */}
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Upcoming Events */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">Upcoming Events</h2>
                <Link to="/Planning" className="text-blue-600 hover:text-blue-800 text-sm font-semibold">
                  View All →
                </Link>
              </div>
              <div className="space-y-4">
                {[
                  { title: 'Youth Worship Night', date: 'Tomorrow, 6:00 PM', location: 'Main Sanctuary' },
                  { title: 'Leadership Training', date: 'Oct 25, 9:00 AM', location: 'Conference Room' },
                  { title: 'Community Outreach', date: 'Oct 28, 2:00 PM', location: 'City Park' },
                  { title: 'Prayer Meeting', date: 'Oct 30, 7:00 PM', location: 'Youth Center' }
                ].map((event, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-300">
                    <div>
                      <h4 className="font-medium text-gray-800">{event.title}</h4>
                      <div className="flex items-center text-sm text-gray-500 mt-1">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                        </svg>
                        {event.date}
                        <svg className="w-4 h-4 ml-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                        {event.location}
                      </div>
                    </div>
                    <button className="text-blue-600 hover:text-blue-800 font-semibold text-sm">
                      Details
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Active Projects */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">Active Projects</h2>
                <Link to="/Projects" className="text-blue-600 hover:text-blue-800 text-sm font-semibold">
                  View All →
                </Link>
              </div>
              <div className="space-y-4">
                {[
                  { title: 'Youth Center Renovation', progress: 75, team: 8, deadline: 'Nov 15' },
                  { title: 'Digital Ministry Platform', progress: 40, team: 5, deadline: 'Dec 10' },
                  { title: 'Leadership Training Program', progress: 90, team: 12, deadline: 'Oct 30' },
                  { title: 'Community Food Drive', progress: 30, team: 15, deadline: 'Nov 25' }
                ].map((project, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-300">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-800">{project.title}</h4>
                      <span className="text-sm text-gray-500">{project.deadline}</span>
                    </div>
                    <div className="mb-2">
                      <div className="flex justify-between text-sm text-gray-500 mb-1">
                        <span>Progress</span>
                        <span>{project.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{project.team} team members</span>
                      <button className="text-blue-600 hover:text-blue-800 font-semibold">
                        Manage
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white mb-8">
            <h2 className="text-2xl font-bold mb-6 text-center">Ministry Overview</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">24</div>
                <div className="text-blue-100">Departments</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">156</div>
                <div className="text-blue-100">Active Volunteers</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">98%</div>
                <div className="text-blue-100">Satisfaction Rate</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">24/7</div>
                <div className="text-blue-100">Support</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;